/**
 * Id input: textarea plus removable chips.
 *
 * Once a summary exists the chips are the safe way to drop an id, because
 * removing one updates the id list and the textarea together in a single
 * action rather than by editing free text.
 */

import React from 'react';
import { Button, Form, Icon, Label, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys, MAX_IDS } from '../constants';
import { ParseFeedback } from '../types';
import styles from '../BulkChargeGuarantee.module.css';

interface IdInputPanelProps {
  rawInput: string;
  ids: number[];
  parseFeedback: ParseFeedback;
  isLoading: boolean;
  isLocked: boolean;
  showStaleWarning: boolean;
  /**
   * Chips only earn their space before a summary exists. Once the grouped
   * table renders it is a strictly better view of the same ids, and a hundred
   * chips stacked above it is duplication with no way to tell which chip is
   * which person. Removal then happens from the row instead.
   */
  showChips: boolean;
  onChange: (value: string) => void;
  onGenerate: () => void;
  onClear: () => void;
  onRemoveId: (id: number) => void;
}

const IdInputPanel: React.FC<IdInputPanelProps> = ({
  rawInput,
  ids,
  parseFeedback,
  isLoading,
  isLocked,
  showStaleWarning,
  showChips,
  onChange,
  onGenerate,
  onClear,
  onRemoveId
}) => {
  const { invalid, duplicates, overflow } = parseFeedback;
  const hasFeedback = invalid.length > 0 || duplicates.length > 0 || overflow > 0;

  return (
    <Segment className={styles.formSegment}>
      <Form onSubmit={onGenerate}>
        <Form.Field>
          <label htmlFor="bulk-charge-ids">
            <Translate value={i18nKeys.FORM.IDS_LABEL} />
          </label>
          {/* Said here rather than only in the explanation block, because this
              is where the decision to paste a mixed list gets made. */}
          <p className={styles.fieldNote}>
            <Translate value={i18nKeys.FORM.MIXED_CHARITIES_NOTE} />
          </p>
          <textarea
            id="bulk-charge-ids"
            className={styles.idTextarea}
            value={rawInput}
            placeholder={I18n.t(i18nKeys.FORM.IDS_PLACEHOLDER)}
            onChange={(event) => onChange(event.target.value)}
            disabled={isLoading || isLocked}
          />
        </Form.Field>

        {ids.length > 0 && showChips && (
          <>
            <div className={styles.chipCount}>
              {I18n.t(i18nKeys.FORM.COUNT, { count: ids.length })}
            </div>
            <div className={styles.chipList}>
              {ids.map((id) => (
                <Label key={id} color="blue">
                  {id}
                  {!isLocked && (
                    <Icon
                      name="delete"
                      link
                      aria-label={I18n.t(i18nKeys.FORM.REMOVE_ID, { id })}
                      onClick={() => onRemoveId(id)}
                    />
                  )}
                </Label>
              ))}
            </div>
          </>
        )}

        {hasFeedback && (
          <div className={styles.parseFeedback}>
            {invalid.length > 0 && (
              <p>
                {I18n.t(i18nKeys.FORM.INVALID_TOKENS, {
                  count: invalid.length,
                  tokens: invalid.slice(0, 10).join(', ')
                })}
              </p>
            )}
            {duplicates.length > 0 && (
              <p>
                {I18n.t(i18nKeys.FORM.DUPLICATES, {
                  count: duplicates.length,
                  ids: duplicates.slice(0, 10).join(', ')
                })}
              </p>
            )}
            {overflow > 0 && (
              <p>{I18n.t(i18nKeys.FORM.OVERFLOW, { max: MAX_IDS, count: overflow })}</p>
            )}
          </div>
        )}

        {showStaleWarning && (
          <div className={styles.parseFeedback}>
            <p>
              <Translate value={i18nKeys.FORM.STALE_SUMMARY} />
            </p>
          </div>
        )}

        {!isLocked && (
          <div className={styles.formActions}>
            <Button primary type="submit" loading={isLoading} disabled={isLoading}>
              <Translate value={i18nKeys.FORM.GENERATE_BUTTON} />
            </Button>
            {rawInput.length > 0 && (
              <Button type="button" onClick={onClear} disabled={isLoading}>
                <Translate value={i18nKeys.FORM.CLEAR_BUTTON} />
              </Button>
            )}
          </div>
        )}
      </Form>
    </Segment>
  );
};

export default IdInputPanel;
